/* eslint-disable no-console */
import type { GetServerSideProps, InferGetServerSidePropsType, NextPage } from 'next';
import { NextSeo } from 'next-seo';
import getConfig from 'next/config';

import { Head } from '@/components';
import { HomepageRenderer } from '@/components/SectionListRenderer';
import { CONNECTING_TO_CONTENTFUL, CONNECTING_TO_REDIS, REDIS_HOMEPAGE_KEY, ThunkDebugName } from '@/constants';
import { getLandingPage } from '@/fetchers';
import type { ILandingPage } from '@/interfaces';
import { checkIsPreviewModeRequest } from '@/utils';
import createServerSidePropsResult, { type IThunk } from '@/utils/createServerSidePropsResult.utils';

const { serverRuntimeConfig, publicRuntimeConfig } = getConfig();

const Home: NextPage<InferGetServerSidePropsType<typeof getServerSideProps>> = ({ homepageData, webVersion }) => {
  // biome-ignore lint/suspicious/noConsoleLog: <explanation>
  console.log(webVersion);

  // Self-referencing canonical + og:url. The homepage seo entry ships no ogUrl, so without
  // this fallback the homepage emits no canonical at all and every ?utm_*/?gclid variant
  // competes as a separate page. Goes through seo.ogUrl because that is what DocumentHead
  // feeds to NextSeo's canonical; must match the address the sitemap advertises.
  const homepageSeo = homepageData?.seo
    ? { ...homepageData.seo, ogUrl: homepageData.seo.ogUrl || publicRuntimeConfig.origin }
    : null;

  return (
    <>
      {homepageSeo ? (
        <Head {...homepageSeo} />
      ) : (
        <NextSeo canonical={publicRuntimeConfig.origin} openGraph={{ url: publicRuntimeConfig.origin }} />
      )}
      <HomepageRenderer sectionList={homepageData?.sectionList} />
    </>
  );
};

interface IHomepageServerSideProps {
  homepageData: ILandingPage | null;
  webVersion: string;
}

type Thunk = IThunk<IHomepageServerSideProps>;

const getRedisUtils: Thunk = async ({ nextJsContext, customContext }) => {
  if (!customContext.serverRuntimeConfig.useDev) {
    const redisUtils = await import('@/utils/redis.utils');

    customContext.getServerPropsCached = redisUtils.getServerPropsCached;
    customContext.cacheServerProps = redisUtils.cacheServerProps;
    customContext.setUrlById = redisUtils.setUrlById;
  }

  nextJsContext.res.setHeader('Cache-Control', 'public, s-maxage=43200, stale-while-revalidate=60');
};

getRedisUtils.debugName = ThunkDebugName.GetRedisUtils;

const getServerPropsCached: Thunk = async ({ customContext }) => {
  const serverPropsCached: IHomepageServerSideProps | null = JSON.parse(
    (await customContext.getServerPropsCached(REDIS_HOMEPAGE_KEY)) || 'null',
  );

  if (serverPropsCached) {
    return {
      props: {
        ...serverPropsCached,
        webVersion: CONNECTING_TO_REDIS,
      },
    };
  }
};

getServerPropsCached.debugName = ThunkDebugName.GetServerPropsCached;

const getHomepageData: Thunk = async ({ nextJsContext, customContext }) => {
  const { serverSideProps } = customContext;

  const isPreviewMode = checkIsPreviewModeRequest(nextJsContext.query);

  serverSideProps.homepageData = await getLandingPage({
    isPreviewMode,
    slug: '',
  });

  if (!isPreviewMode && serverSideProps.homepageData) {
    customContext.setUrlById(serverSideProps.homepageData.sys.id, REDIS_HOMEPAGE_KEY);
    customContext.cacheServerProps(REDIS_HOMEPAGE_KEY, serverSideProps);
  }

  return {
    props: {
      ...serverSideProps,
    },
  };
};

getHomepageData.debugName = 'GET_HOMEPAGE_DATA';

export const getServerSideProps: GetServerSideProps<IHomepageServerSideProps> = async (context) => {
  return createServerSidePropsResult<IHomepageServerSideProps>({
    context,
    customContext: {
      serverSideProps: {
        homepageData: null,
        webVersion: CONNECTING_TO_CONTENTFUL,
      },
      serverRuntimeConfig,
      getServerPropsCached: () => null,
      cacheServerProps: () => null,
      setUrlById: () => null,
    },
    thunks: [getRedisUtils, getServerPropsCached, getHomepageData],
  });
};

export default Home;
